// src/pages/Home.jsx
import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Box,
  TextField,
  InputAdornment,
  Paper,
  Chip,
  IconButton,
  useMediaQuery,
  useTheme,
  alpha,
  Stack,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Fab,
  Badge,
} from '@mui/material';
import {
  Search,
  LocalShipping,
  VerifiedUser,
  Payment,
  SupportAgent,
  ArrowRightAlt,
  Public,
  TrendingUp,
  Security,
  CheckCircle,
  ExpandMore,
  RocketLaunch,
  PlayArrow,
  Star,
  Diversity3,
  Insights,
  Fingerprint,
  ShoppingCart,
  Handshake,
  TrackChanges,
  Celebration,
  AccessTime,
  Chat,
  Close,
} from '@mui/icons-material';
import { Link, useNavigate } from 'react-router-dom';
import { fruitsAPI } from '../services/api';
import toast from 'react-hot-toast';
import FruitCard from '../components/Fruits/FruitCard';
import ChatWindow from '../components/Chat/ChatWindow'; // Import ChatWindow
import { motion, AnimatePresence } from 'framer-motion';

const Home = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [fruits, setFruits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hoveredFeature, setHoveredFeature] = useState(null);
  const [typingIndex, setTypingIndex] = useState(0);
  const [typingText, setTypingText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [isTyping, setIsTyping] = useState(true);
  const [activeStep, setActiveStep] = useState(0);
  const [chatOpen, setChatOpen] = useState(false); // Chat dialog state
  const [unreadMessages, setUnreadMessages] = useState(3); // Example unread count
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const featuresRef = useRef(null);
  const stepsRef = useRef(null);
  const typingRef = useRef(null);

  const typingWords = [
    "Global Fruit Markets",
    "Verified Suppliers",
    "Fresh Produce Network",
    "Secure Trade Platform"
  ];

  useEffect(() => {
    fetchFruits();
  }, []);

  useEffect(() => {
    let timeout;
    const type = () => {
      const currentWord = typingWords[typingIndex];
      
      if (typingText === '') {
        setIsTyping(true);
      }
      
      if (!isDeleting && typingText === currentWord) {
        timeout = setTimeout(() => {
          setIsDeleting(true);
        }, 1500);
        return;
      }
      
      if (isDeleting && typingText === '') {
        setIsDeleting(false);
        setTypingIndex((prev) => (prev + 1) % typingWords.length);
        return;
      }
      
      const delta = isDeleting ? 50 : 100;
      const nextText = isDeleting 
        ? currentWord.substring(0, typingText.length - 1)
        : currentWord.substring(0, typingText.length + 1);
      
      setTypingText(nextText);
      
      if (isDeleting && nextText === '') {
        setIsTyping(false);
        timeout = setTimeout(() => {
          setIsTyping(true);
          timeout = setTimeout(type, 500);
        }, 500);
      } else {
        timeout = setTimeout(type, delta);
      }
    };
    
    timeout = setTimeout(type, 500);
    
    return () => {
      clearTimeout(timeout);
    };
  }, [typingText, isDeleting, typingIndex]);

  // Auto-rotate steps for desktop
  useEffect(() => {
    if (isMobile) return;
    
    const interval = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % tradingSteps.length);
    }, 3000);
    
    return () => clearInterval(interval);
  }, [isMobile]);

  const fetchFruits = async () => {
    try {
      const response = await fruitsAPI.getAll();
      setFruits(response.data.slice(0, 8));
    } catch (error) {
      console.error('Error fetching fruits:', error);
      toast.error('Failed to load fruits');
    } finally {
      setLoading(false);
    }
  };

  const features = [
    {
      icon: <LocalShipping sx={{ fontSize: 40 }} />,
      title: 'Global Shipping',
      description: 'Direct shipments from origin to destination ports',
      color: '#2e7d32',
      stats: '150+ Countries',
    },
    {
      icon: <VerifiedUser sx={{ fontSize: 40 }} />,
      title: 'Verified Partners',
      description: 'Trade only with verified and trusted businesses',
      color: '#1976d2',
      stats: '500+ Suppliers',
    },
    {
      icon: <Security sx={{ fontSize: 40 }} />,
      title: 'Secure Payments',
      description: 'Protected payment workflow with advance system',
      color: '#ed6c02',
      stats: '$2B+ Processed',
    },
    {
      icon: <SupportAgent sx={{ fontSize: 40 }} />,
      title: '24/7 Support',
      description: 'Round-the-clock support via chat and WhatsApp',
      color: '#9c27b0',
      stats: '10 Min Response',
    },
  ];

  const tradingSteps = [
    { 
      number: '01', 
      title: 'Register & Verify', 
      description: 'Create your account and complete business verification',
      icon: <Fingerprint />,
      color: '#4caf50',
      duration: '2 mins',
      details: ['Email registration', 'Business verification', 'Profile setup']
    },
    { 
      number: '02', 
      title: 'Browse Marketplace', 
      description: 'Explore thousands of quality fruits from verified growers',
      icon: <ShoppingCart />,
      color: '#2196f3',
      duration: 'Instant',
      details: ['Advanced filters', 'Price comparison', 'Supplier ratings']
    },
    { 
      number: '03', 
      title: 'Connect & Negotiate', 
      description: 'Direct communication with suppliers for best deals',
      icon: <Handshake />,
      color: '#ff9800',
      duration: '24h response',
      details: ['Secure messaging', 'Bulk discounts', 'Custom requests']
    },
    { 
      number: '04', 
      title: 'Secure Payment', 
      description: 'Protected payment process with escrow service',
      icon: <Payment />,
      color: '#9c27b0',
      duration: 'Secure escrow',
      details: ['Multiple currencies', 'Escrow protection', 'Invoice system']
    },
    { 
      number: '05', 
      title: 'Track Shipment', 
      description: 'Real-time tracking from farm to destination',
      icon: <TrackChanges />,
      color: '#00bcd4',
      duration: 'Live updates',
      details: ['GPS tracking', 'Document management', 'ETA alerts']
    },
    { 
      number: '06', 
      title: 'Receive & Review', 
      description: 'Quality inspection and supplier review system',
      icon: <Celebration />,
      color: '#e91e63',
      duration: 'Quality assured',
      details: ['Quality check', 'Leave reviews', 'Build reputation']
    },
  ];

  const trendingCategories = [
    { name: 'Tropical Fruits', count: '120+', color: '#ff9800', icon: '🥭' },
    { name: 'Citrus', count: '85+', color: '#ffc107', icon: '🍊' },
    { name: 'Berries', count: '65+', color: '#e91e63', icon: '🫐' },
    { name: 'Melons', count: '45+', color: '#4caf50', icon: '🍈' },
    { name: 'Stone Fruits', count: '75+', color: '#9c27b0', icon: '🍑' },
    { name: 'Exotics', count: '50+', color: '#2196f3', icon: '🥝' },
  ];

  const stats = [
    { value: '500+', label: 'Verified Suppliers', icon: '🏭', change: '+15%' },
    { value: '150+', label: 'Countries', icon: '🌎', change: '+10%' },
    { value: '24h', label: 'Avg. Response Time', icon: '⚡', change: '-40%' },
    { value: '$2B+', label: 'Annual Trade', icon: '💰', change: '+25%' },
  ];

  const testimonials = [
    {
      name: 'Alex Chen',
      role: 'Import Manager, FreshMart',
      content: 'Reduced sourcing time by 70% and improved supplier quality.',
      rating: 5,
      avatar: 'AC',
    },
    {
      name: 'Maria Rodriguez',
      role: 'CEO, Tropical Fruits Inc.',
      content: 'The platform helped us expand to 3 new countries in 6 months.',
      rating: 5,
      avatar: 'MR',
    },
    {
      name: 'James Wilson',
      role: 'Procurement Director, Global Foods',
      content: 'Secure payments and real-time tracking transformed our operations.',
      rating: 5,
      avatar: 'JW',
    },
  ];

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      navigate(`/fruits?search=${encodeURIComponent(searchTerm)}`);
    }
  };

  const scrollToFeatures = () => {
    featuresRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToSteps = () => {
    stepsRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleChatOpen = () => {
    setChatOpen(true);
    setUnreadMessages(0); // Clear unread messages when opened
  };

  const handleChatClose = () => {
    setChatOpen(false);
  };

  // Simulate receiving new messages
  useEffect(() => {
    if (!chatOpen) {
      const interval = setInterval(() => {
        // Simulate receiving new messages when chat is closed
        setUnreadMessages(prev => Math.min(prev + 1, 99));
      }, 30000); // Every 30 seconds

      return () => clearInterval(interval);
    }
  }, [chatOpen]);

  return (
    <Box sx={{ overflow: 'hidden', position: 'relative' }}>
      {/* Floating Chat Button */}
      <motion.div
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.5, delay: 1 }}
        style={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          zIndex: 1000,
        }}
      >
        <Badge 
          badgeContent={unreadMessages} 
          color="error"
          overlap="circular"
          sx={{
            '& .MuiBadge-badge': {
              fontSize: '0.75rem',
              height: 20,
              minWidth: 20,
            },
          }}
        >
          <Fab
            color="primary"
            onClick={handleChatOpen}
            sx={{
              width: 60,
              height: 60,
              background: 'linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)',
              boxShadow: '0 8px 25px rgba(76, 175, 80, 0.4)',
              '&:hover': {
                background: 'linear-gradient(135deg, #2e7d32 0%, #4caf50 100%)',
                boxShadow: '0 12px 30px rgba(76, 175, 80, 0.6)',
                transform: 'scale(1.05)',
              },
              transition: 'all 0.3s',
            }}
          >
            <Chat sx={{ fontSize: 28 }} />
          </Fab>
        </Badge>
      </motion.div>

      {/* Chat Dialog */}
      <Dialog
        open={chatOpen}
        onClose={handleChatClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            height: '80vh',
            maxHeight: '700px',
            borderRadius: 3,
            overflow: 'hidden',
          }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: 'primary.main', 
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 2,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: 'white', color: 'primary.main' }}>
              <Chat />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight="bold">
                Live Support Chat
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                24/7 Customer Support
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={handleChatClose} sx={{ color: 'white' }}>
            <Close />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ p: 0 }}>
          <ChatWindow enquiryId="demo-123" />
        </DialogContent>
        
        <DialogActions sx={{ 
          p: 2, 
          bgcolor: 'grey.50',
          justifyContent: 'space-between',
          borderTop: '1px solid',
          borderColor: 'divider',
        }}>
          <Typography variant="caption" color="text.secondary">
            Need urgent help? Call us at +1-800-FRUIT-MKT
          </Typography>
          <Button 
            variant="outlined" 
            size="small"
            onClick={handleChatClose}
          >
            Close Chat
          </Button>
        </DialogActions>
      </Dialog>

      {/* Enhanced Hero Section */}
      <Box
        sx={{
          position: 'relative',
          background: 'linear-gradient(135deg, #0d2f12 0%, #1b5e20 30%, #2e7d32 100%)',
          color: 'white',
          pt: { xs: 6, md: 10 },
          pb: { xs: 8, md: 12 },
          minHeight: { xs: 'auto', md: '90vh' },
          display: 'flex',
          alignItems: 'center',
          overflow: 'hidden',
        }}
      >
        {/* Animated Background Elements */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            overflow: 'hidden',
            pointerEvents: 'none',
          }}
        >
          {[...Array(8)].map((_, i) => (
            <motion.div
              key={i}
              animate={{
                x: [0, Math.random() * 100 - 50],
                y: [0, Math.random() * 100 - 50],
              }}
              transition={{
                duration: Math.random() * 20 + 20,
                repeat: Infinity,
                repeatType: "reverse",
                delay: i * 2,
              }}
              style={{
                position: 'absolute',
                width: Math.random() * 100 + 50,
                height: Math.random() * 100 + 50,
                borderRadius: '50%',
                background: `radial-gradient(circle, ${alpha('#4caf50', 0.05)} 0%, transparent 70%)`,
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
              }}
            />
          ))}
        </Box>

        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box sx={{ maxWidth: 800 }}>
                {/* Badge */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3, flexWrap: 'wrap' }}>
                    <Chip 
                      label="TRUSTED GLOBAL PLATFORM" 
                      icon={<Public />}
                      sx={{ 
                        backgroundColor: alpha('#ffeb3b', 0.2), 
                        color: '#ffeb3b',
                        fontWeight: 600,
                        py: 0.8,
                        fontSize: { xs: '0.7rem', sm: '0.875rem' },
                      }}
                    />
                    <Chip 
                      label="5,000+ TRADERS" 
                      sx={{ 
                        backgroundColor: alpha('#fff', 0.1), 
                        color: 'white',
                        fontWeight: 600,
                        py: 0.8,
                        fontSize: { xs: '0.7rem', sm: '0.875rem' },
                      }}
                    />
                  </Box>
                </motion.div>
                
                {/* Main Title with Enhanced Typing Animation */}
                <Box sx={{ mb: 3 }}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.1 }}
                  >
                    <Typography 
                      variant={isMobile ? "h3" : isTablet ? "h2" : "h1"} 
                      component="h1" 
                      sx={{
                        fontWeight: 'bold',
                        lineHeight: 1.2,
                        fontSize: { 
                          xs: '2.2rem', 
                          sm: '3rem', 
                          md: '3.5rem',
                          lg: '4rem'
                        },
                        mb: 1,
                      }}
                    >
                      Direct Access to
                    </Typography>
                  </motion.div>
                  
                  <Box 
                    ref={typingRef}
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      minHeight: { xs: '3.5rem', sm: '4rem', md: '5rem' },
                      position: 'relative',
                    }}
                  >
                    <Typography 
                      variant={isMobile ? "h3" : isTablet ? "h2" : "h1"} 
                      sx={{
                        fontWeight: 'bold',
                        background: 'linear-gradient(90deg, #ffeb3b 0%, #ff9800 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        lineHeight: 1.2,
                        fontSize: { 
                          xs: '2.2rem', 
                          sm: '3rem', 
                          md: '3.5rem',
                          lg: '4rem'
                        },
                        position: 'relative',
                        display: 'inline-block',
                        textShadow: '0 4px 12px rgba(0,0,0,0.3)',
                      }}
                    >
                      {typingText}
                      <motion.span
                        animate={{ opacity: [1, 0, 1] }}
                        transition={{ duration: 1, repeat: Infinity }}
                        style={{
                          position: 'absolute',
                          right: -10,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          width: 3,
                          height: '70%',
                          backgroundColor: '#ff9800',
                          display: isTyping ? 'block' : 'none',
                        }}
                      />
                    </Typography>
                    
                    {/* Current word indicator */}
                    <Box sx={{ 
                      position: 'absolute', 
                      bottom: -25, 
                      left: 0,
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 1 
                    }}>
                      <Box sx={{ 
                        width: 8, 
                        height: 8, 
                        borderRadius: '50%', 
                        backgroundColor: isTyping ? '#ff9800' : alpha('#ff9800', 0.3),
                        animation: isTyping ? 'pulse 1s infinite' : 'none',
                        '@keyframes pulse': {
                          '0%, 100%': { opacity: 1 },
                          '50%': { opacity: 0.5 },
                        }
                      }} />
                      <Typography variant="caption" sx={{ opacity: 0.7, fontSize: '0.75rem' }}>
                        {typingWords[typingIndex]}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                
                {/* Subtitle */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                >
                  <Typography 
                    variant={isMobile ? "body1" : "h6"} 
                    paragraph 
                    sx={{ 
                      mb: 4, 
                      opacity: 0.9,
                      fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' },
                      maxWidth: '90%',
                    }}
                  >
                    Connect with verified fruit exporters worldwide. Secure transactions, 
                    real-time tracking, and premium quality assurance for your business.
                  </Typography>
                </motion.div>
                
                {/* Interactive Search Bar */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                >
                  <Paper
                    component="form"
                    onSubmit={handleSearch}
                    sx={{
                      p: { xs: 1.5, sm: 2 },
                      mb: 4,
                      backgroundColor: alpha('#fff', 0.08),
                      backdropFilter: 'blur(20px)',
                      borderRadius: 4,
                      border: '1px solid rgba(255,255,255,0.15)',
                      boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        backgroundColor: alpha('#fff', 0.12),
                        boxShadow: '0 25px 80px rgba(0,0,0,0.4)',
                      },
                    }}
                  >
                    <Grid container spacing={1} alignItems="center">
                      <Grid item xs>
                        <TextField
                          fullWidth
                          placeholder="Search fruits, varieties, origins..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              color: 'white',
                              fontSize: { xs: '0.9rem', sm: '1rem' },
                              '& fieldset': { border: 'none' },
                              '&:hover fieldset': { border: 'none' },
                              '&.Mui-focused fieldset': { border: 'none' },
                            },
                            '& .MuiInputBase-input::placeholder': {
                              color: alpha('#fff', 0.6),
                            },
                          }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Search sx={{ color: alpha('#fff', 0.7), fontSize: { xs: 20, sm: 24 } }} />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                      <Grid item xs="auto">
                        <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                          <Button
                            type="submit"
                            variant="contained"
                            size={isMobile ? "medium" : "large"}
                            sx={{
                              background: 'linear-gradient(90deg, #ffeb3b 0%, #ff9800 100%)',
                              color: '#1b5e20',
                              fontWeight: 'bold',
                              borderRadius: 3,
                              px: { xs: 2.5, sm: 4 },
                              py: { xs: 1.2, sm: 1.5 },
                              whiteSpace: 'nowrap',
                              boxShadow: '0 10px 30px rgba(255, 152, 0, 0.3)',
                              fontSize: { xs: '0.875rem', sm: '1rem' },
                              '&:hover': {
                                background: 'linear-gradient(90deg, #ff9800 0%, #ffeb3b 100%)',
                                boxShadow: '0 15px 40px rgba(255, 152, 0, 0.4)',
                              },
                            }}
                          >
                            Search Market
                          </Button>
                        </motion.div>
                      </Grid>
                    </Grid>
                  </Paper>
                </motion.div>
                
                {/* Action Buttons */}
                <Stack 
                  direction={{ xs: 'column', sm: 'row' }} 
                  spacing={2} 
                  sx={{ mb: 4 }}
                >
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: 0.4 }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Button
                      onClick={scrollToSteps}
                      variant="contained"
                      size={isMobile ? "medium" : "large"}
                      sx={{
                        background: 'linear-gradient(90deg, #4caf50 0%, #2e7d32 100%)',
                        color: 'white',
                        fontWeight: 'bold',
                        borderRadius: 3,
                        py: 1.5,
                        px: { xs: 3, sm: 4 },
                        boxShadow: '0 10px 30px rgba(46, 125, 50, 0.3)',
                        '&:hover': {
                          background: 'linear-gradient(90deg, #2e7d32 0%, #4caf50 100%)',
                        },
                      }}
                      endIcon={<ArrowRightAlt sx={{ fontSize: { xs: 20, sm: 24 } }} />}
                    >
                      Start Trading Now
                    </Button>
                  </motion.div>
                  
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: 0.5 }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Button
                      onClick={scrollToFeatures}
                      variant="outlined"
                      size={isMobile ? "medium" : "large"}
                      sx={{
                        borderColor: alpha('#fff', 0.3),
                        color: 'white',
                        borderRadius: 3,
                        py: 1.5,
                        px: { xs: 3, sm: 4 },
                        backdropFilter: 'blur(10px)',
                        '&:hover': {
                          borderColor: alpha('#fff', 0.6),
                          backgroundColor: alpha('#fff', 0.1),
                        },
                      }}
                      startIcon={<RocketLaunch />}
                    >
                      Explore Features
                    </Button>
                  </motion.div>
                </Stack>
                
                {/* Interactive Stats */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.6 }}
                >
                  <Box sx={{ mt: { xs: 4, sm: 6 } }}>
                    <Typography variant="body2" sx={{ opacity: 0.7, mb: 2, textTransform: 'uppercase', letterSpacing: 1, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      Platform Growth This Month
                    </Typography>
                    <Grid container spacing={{ xs: 2, sm: 3 }}>
                      {stats.map((stat, index) => (
                        <Grid item xs={6} sm={3} key={index}>
                          <Box 
                            sx={{ 
                              textAlign: 'left',
                              p: { xs: 1, sm: 2 },
                              borderRadius: 2,
                              backgroundColor: alpha('#fff', 0.05),
                              backdropFilter: 'blur(10px)',
                              border: '1px solid rgba(255,255,255,0.1)',
                              transition: 'all 0.3s',
                              '&:hover': {
                                backgroundColor: alpha('#fff', 0.1),
                                transform: 'translateY(-2px)',
                              },
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mb: 0.5 }}>
                              <Typography variant="h6" fontWeight="bold" sx={{ fontSize: { xs: '1.1rem', sm: '1.5rem', md: '1.8rem' } }}>
                                {stat.value}
                              </Typography>
                              <Chip 
                                label={stat.change} 
                                size="small" 
                                sx={{ 
                                  backgroundColor: stat.change.startsWith('+') ? alpha('#4caf50', 0.2) : alpha('#ff9800', 0.2),
                                  color: stat.change.startsWith('+') ? '#4caf50' : '#ff9800',
                                  fontWeight: 'bold',
                                  fontSize: '0.6rem',
                                  height: 18,
                                }}
                              />
                            </Box>
                            <Typography variant="caption" sx={{ opacity: 0.8, display: 'flex', alignItems: 'center', gap: 0.5, fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                              <Box component="span" sx={{ fontSize: '1rem' }}>{stat.icon}</Box> {stat.label}
                            </Typography>
                            <Box 
                              sx={{ 
                                height: 3, 
                                width: '100%', 
                                backgroundColor: alpha('#fff', 0.1), 
                                borderRadius: 2,
                                mt: 1,
                                overflow: 'hidden',
                              }}
                            >
                              <motion.div
                                initial={{ width: 0 }}
                                animate={{ width: stat.change.startsWith('+') ? '70%' : '40%' }}
                                transition={{ duration: 1, delay: 0.7 + index * 0.1 }}
                                style={{
                                  height: '100%',
                                  background: 'linear-gradient(90deg, #4caf50, #8bc34a)',
                                  borderRadius: 2,
                                }}
                              />
                            </Box>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                </motion.div>
              </Box>
            </Grid>
            
            {/* Hero Visual */}
            <Grid item xs={12} md={6}>
              <Box sx={{ 
                display: { xs: 'none', md: 'flex' },
                justifyContent: 'center',
                alignItems: 'center',
                position: 'relative',
                height: '100%',
                minHeight: 600,
              }}>
                <Box
                  sx={{
                    position: 'relative',
                    width: '100%',
                    maxWidth: 600,
                    height: '100%',
                  }}
                >
                  {/* Main Image */}
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.8 }}
                    style={{ width: '100%', height: '100%' }}
                  >
                    <Box
                      sx={{
                        position: 'relative',
                        width: '100%',
                        height: '100%',
                        borderRadius: '40% 60% 60% 40% / 50% 50% 70% 30%',
                        overflow: 'hidden',
                        boxShadow: '0 40px 80px rgba(0,0,0,0.4)',
                        background: 'linear-gradient(135deg, #1b5e20 0%, #4caf50 100%)',
                      }}
                    >
                      <Box
                        component="img"
                        src="https://images.unsplash.com/photo-1610832958506-aa56368176cf?w=1200&auto=format&fit=crop&q=80"
                        alt="Global Fruit Trading"
                        sx={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          mixBlendMode: 'multiply',
                          opacity: 0.9,
                        }}
                      />
                    </Box>
                  </motion.div>
                  
                  {/* Interactive Floating Elements */}
                  {[...Array(4)].map((_, i) => (
                    <motion.div
                      key={i}
                      initial={{ scale: 0, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ duration: 0.5, delay: 0.5 + i * 0.1 }}
                      whileHover={{ scale: 1.1 }}
                      style={{
                        position: 'absolute',
                        top: `${20 + i * 15}%`,
                        left: i % 2 === 0 ? '5%' : '80%',
                        cursor: 'pointer',
                      }}
                    >
                      <Paper
                        sx={{
                          p: 2,
                          borderRadius: 4,
                          backgroundColor: alpha('#fff', 0.1),
                          backdropFilter: 'blur(10px)',
                          border: '1px solid rgba(255,255,255,0.2)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          maxWidth: 180,
                          transition: 'all 0.3s',
                          '&:hover': {
                            backgroundColor: alpha('#fff', 0.2),
                            transform: 'translateY(-5px)',
                          },
                        }}
                      >
                        <Avatar sx={{ 
                          bgcolor: ['#ff9800', '#4caf50', '#2196f3', '#9c27b0'][i],
                          width: { xs: 32, sm: 40 },
                          height: { xs: 32, sm: 40 },
                        }}>
                          {['🍊', '🥭', '🍇', '🥝'][i]}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight="bold" color="white" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                            {['Fresh Oranges', 'Premium Mangoes', 'Quality Grapes', 'Exotic Fruits'][i]}
                          </Typography>
                          <Typography variant="caption" sx={{ opacity: 0.8, color: 'white', fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                            {['From Spain', 'From India', 'From Italy', 'From Thailand'][i]}
                          </Typography>
                        </Box>
                      </Paper>
                    </motion.div>
                  ))}
                </Box>
              </Box>
            </Grid>
          </Grid>
          
          {/* Scroll Indicator */}
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            style={{ 
              position: 'absolute', 
              bottom: 20, 
              left: '50%', 
              transform: 'translateX(-50%)',
              display: { xs: 'none', md: 'block' }
            }}
          >
            <IconButton
              onClick={scrollToFeatures}
              sx={{
                color: 'white',
                border: `2px solid ${alpha('#fff', 0.3)}`,
                borderRadius: '50%',
                width: 50,
                height: 50,
                '&:hover': {
                  backgroundColor: alpha('#fff', 0.1),
                },
              }}
            >
              <ExpandMore />
            </IconButton>
          </motion.div>
        </Container>
      </Box>

      {/* Features Section */}
      <Container 
        ref={featuresRef}
        maxWidth="xl" 
        sx={{ 
          py: { xs: 6, md: 10 }, 
          px: { xs: 2, sm: 3, md: 4 },
          position: 'relative',
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 8 } }}>
            <Chip 
              label="WHY CHOOSE US" 
              sx={{ 
                backgroundColor: alpha('#4caf50', 0.1),
                color: '#4caf50',
                fontWeight: 600,
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                mb: 2,
                py: 0.8,
              }}
            />
            <Typography 
              variant={isMobile ? "h4" : "h2"} 
              gutterBottom 
              sx={{ 
                fontWeight: 'bold',
                fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' },
              }}
            >
              Trade Smarter, Grow Faster
            </Typography>
            <Typography 
              variant={isMobile ? "body1" : "h6"} 
              color="text.secondary"
              sx={{ maxWidth: 700, mx: 'auto', fontSize: { xs: '0.9rem', sm: '1rem', md: '1.25rem' } }}
            >
              Everything you need to streamline your fruit trading operations
            </Typography>
          </Box>
        </motion.div>
        
        <Grid container spacing={3}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ scale: 1.05 }}
              >
                <Card
                  onMouseEnter={() => setHoveredFeature(index)}
                  onMouseLeave={() => setHoveredFeature(null)}
                  sx={{
                    height: '100%',
                    textAlign: 'center',
                    p: { xs: 2, sm: 3 },
                    borderRadius: 4,
                    border: `2px solid ${alpha(feature.color, 0.1)}`,
                    transition: 'all 0.3s',
                    backgroundColor: 'white',
                    position: 'relative',
                    overflow: 'hidden',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: `0 20px 40px ${alpha(feature.color, 0.15)}`,
                    },
                  }}
                  elevation={0}
                >
                  <Box
                    sx={{
                      width: { xs: 60, sm: 70 },
                      height: { xs: 60, sm: 70 },
                      borderRadius: '50%',
                      background: `linear-gradient(135deg, ${alpha(feature.color, 0.1)} 0%, ${alpha(feature.color, 0.2)} 100%)`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 20px',
                      transition: 'all 0.3s',
                    }}
                  >
                    <Box sx={{ color: feature.color }}>
                      {feature.icon}
                    </Box>
                  </Box>
                  
                  <Typography 
                    variant="h6" 
                    gutterBottom 
                    fontWeight="bold"
                    sx={{ 
                      fontSize: { xs: '1rem', sm: '1.25rem' },
                      mb: 1,
                    }}
                  >
                    {feature.title}
                  </Typography>
                  
                  <Typography 
                    variant="body2" 
                    color="text.secondary"
                    sx={{ mb: 2, fontSize: { xs: '0.875rem', sm: '1rem' } }}
                  >
                    {feature.description}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                    <TrendingUp sx={{ color: feature.color, fontSize: 18 }} />
                    <Typography 
                      variant="caption" 
                      fontWeight="bold"
                      sx={{ color: feature.color, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                    >
                      {feature.stats}
                    </Typography>
                  </Box>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Trending Categories */}
      <Box sx={{ backgroundColor: 'grey.50', py: { xs: 6, md: 10 } }}>
        <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: { xs: 'flex-start', sm: 'center' }, 
              mb: 4,
              flexDirection: { xs: 'column', sm: 'row' },
              gap: { xs: 2, sm: 0 },
            }}>
              <Box>
                <Typography 
                  variant={isMobile ? "h4" : "h2"} 
                  fontWeight="bold"
                  sx={{ 
                    mb: 1,
                    fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' }
                  }}
                >
                  Explore Trending
                  <Box component="span" sx={{ color: 'primary.main', display: 'block' }}>
                    Fruit Categories
                  </Box>
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                  Discover the most sought-after fruits in global markets
                </Typography>
              </Box>
              <Button
                component={Link}
                to="/categories"
                variant="outlined"
                size={isMobile ? "medium" : "large"}
                endIcon={<ArrowRightAlt />}
                sx={{
                  borderRadius: 3,
                  px: { xs: 2, sm: 4 },
                  py: 1,
                  borderWidth: 2,
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  '&:hover': {
                    borderWidth: 2,
                  },
                }}
              >
                View All
              </Button>
            </Box>
          </motion.div>
          
          <Grid container spacing={2}>
            {trendingCategories.map((category, index) => (
              <Grid item xs={6} sm={4} md={2} key={index}>
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  whileHover={{ scale: 1.05 }}
                >
                  <Card
                    sx={{
                      textAlign: 'center',
                      p: { xs: 1.5, sm: 2 },
                      borderRadius: 4,
                      backgroundColor: 'white',
                      border: `2px solid ${alpha(category.color, 0.1)}`,
                      transition: 'all 0.3s',
                      cursor: 'pointer',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      '&:hover': {
                        backgroundColor: alpha(category.color, 0.05),
                        transform: 'translateY(-4px)',
                      },
                    }}
                  >
                    <Typography variant="h2" sx={{ mb: 1, fontSize: { xs: '2rem', sm: '2.5rem' } }}>
                      {category.icon}
                    </Typography>
                    <Typography variant="h6" fontWeight="bold" sx={{ mb: 0.5, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                      {category.name}
                    </Typography>
                    <Box
                      sx={{
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 20,
                        backgroundColor: alpha(category.color, 0.1),
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                      }}
                    >
                      <Typography variant="caption" fontWeight="bold" sx={{ color: category.color }}>
                        {category.count}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                        Varieties
                      </Typography>
                    </Box>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Popular Fruits Section */}
      <Box sx={{ py: { xs: 6, md: 10 } }}>
        <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: { xs: 'flex-start', sm: 'center' }, 
              mb: 4,
              flexDirection: { xs: 'column', sm: 'row' },
              gap: { xs: 2, sm: 0 },
            }}>
              <Box>
                <Typography 
                  variant={isMobile ? "h4" : "h2"} 
                  fontWeight="bold"
                  sx={{ 
                    mb: 1,
                    fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' }
                  }}
                >
                  Premium Quality
                  <Box component="span" sx={{ color: 'primary.main', display: 'block' }}>
                    Fresh from Farms
                  </Box>
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                  Handpicked quality fruits from verified growers worldwide
                </Typography>
              </Box>
              <Button
                component={Link}
                to="/fruits"
                variant="contained"
                size={isMobile ? "medium" : "large"}
                endIcon={<ArrowRightAlt />}
                sx={{
                  borderRadius: 3,
                  px: { xs: 2, sm: 4 },
                  py: 1,
                  background: 'linear-gradient(90deg, #4caf50 0%, #2e7d32 100%)',
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  '&:hover': {
                    background: 'linear-gradient(90deg, #2e7d32 0%, #4caf50 100%)',
                  },
                }}
              >
                Explore All Fruits
              </Button>
            </Box>
          </motion.div>
          
          {loading ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h6" color="text.secondary">
                Loading fresh fruits...
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={3}>
              {fruits.map((fruit, index) => (
                <Grid item xs={12} sm={6} md={3} key={fruit.id}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                  >
                    <FruitCard fruit={fruit} />
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          )}
        </Container>
      </Box>

      {/* Enhanced How It Works Section - Modern Design */}
      <Box 
        ref={stepsRef}
        sx={{ 
          position: 'relative',
          py: { xs: 8, md: 15 },
          minHeight: { xs: 'auto', md: '100vh' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        {/* Modern Background with Gradient */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, #0c2b0f 0%, #1a5c1f 30%, #2e7d32 100%)',
            zIndex: 0,
          }}
        />
        
        {/* Animated Background Pattern */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `
              radial-gradient(circle at 20% 30%, rgba(255, 255, 255, 0.05) 0%, transparent 50%),
              radial-gradient(circle at 80% 70%, rgba(255, 255, 255, 0.03) 0%, transparent 50%),
              radial-gradient(circle at 40% 80%, rgba(255, 255, 255, 0.04) 0%, transparent 50%)
            `,
            zIndex: 1,
          }}
        />
        
        {/* Floating Animated Elements */}
        {[...Array(10)].map((_, i) => (
          <motion.div
            key={i}
            animate={{
              x: [0, Math.random() * 200 - 100],
              y: [0, Math.random() * 200 - 100],
              rotate: [0, 360],
            }}
            transition={{
              duration: Math.random() * 30 + 20,
              repeat: Infinity,
              repeatType: "reverse",
              ease: "linear",
            }}
            style={{
              position: 'absolute',
              width: Math.random() * 60 + 20,
              height: Math.random() * 60 + 20,
              background: `radial-gradient(circle, ${alpha('#ffeb3b', Math.random() * 0.1 + 0.02)} 0%, transparent 70%)`,
              borderRadius: '50%',
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              zIndex: 1,
              pointerEvents: 'none',
            }}
          />
        ))}
        
        <Container 
          maxWidth="xl" 
          sx={{ 
            position: 'relative', 
            zIndex: 2,
            px: { xs: 2, sm: 3, md: 4 },
          }}
        >
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            {/* Section Header - Modern Design */}
            <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 10 } }}>
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <Chip 
                  label="START TRADING" 
                  sx={{ 
                    backgroundColor: alpha('#ffeb3b', 0.2),
                    color: '#ffeb3b',
                    fontWeight: 700,
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    mb: 3,
                    py: 1,
                    px: 2,
                    letterSpacing: 1,
                  }}
                />
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1 }}
              >
                <Typography 
                  variant={isMobile ? "h3" : "h1"} 
                  sx={{ 
                    fontWeight: 800,
                    color: 'white',
                    fontSize: { 
                      xs: '2.5rem', 
                      sm: '3.5rem', 
                      md: '4.5rem',
                      lg: '5rem'
                    },
                    lineHeight: 1.1,
                    mb: 3,
                  }}
                >
                  Start Trading in{' '}
                  <Box 
                    component="span" 
                    sx={{ 
                      background: 'linear-gradient(90deg, #ffeb3b 0%, #ff9800 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      position: 'relative',
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        bottom: -10,
                        left: '10%',
                        width: '80%',
                        height: 4,
                        background: 'linear-gradient(90deg, #ffeb3b 0%, #ff9800 100%)',
                        borderRadius: 2,
                      }
                    }}
                  >
                    6 Simple Steps
                  </Box>
                </Typography>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <Typography 
                  variant={isMobile ? "body1" : "h5"} 
                  sx={{ 
                    color: alpha('#fff', 0.85),
                    maxWidth: 800, 
                    mx: 'auto', 
                    fontSize: { 
                      xs: '1.1rem', 
                      sm: '1.3rem', 
                      md: '1.5rem' 
                    },
                    lineHeight: 1.6,
                    mb: 6,
                    fontWeight: 400,
                  }}
                >
                  Join thousands of successful traders with our streamlined process. 
                  From registration to delivery, we've made global fruit trading effortless.
                </Typography>
              </motion.div>
            </Box>
          </motion.div>
          
          {/* Desktop Layout - Modern Grid with Animation */}
          {!isMobile && (
            <Box sx={{ position: 'relative', minHeight: 600 }}>
              {/* Animated Connection Lines */}
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: 0,
                  right: 0,
                  height: 2,
                  background: `linear-gradient(90deg, transparent, ${alpha('#ffeb3b', 0.3)}, transparent)`,
                  transform: 'translateY(-50%)',
                  zIndex: 1,
                }}
              />
              
              {/* Steps Grid */}
              <Grid container spacing={{ xs: 3, md: 4 }} justifyContent="center">
                {tradingSteps.map((step, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <motion.div
                      initial={{ opacity: 0, y: 50, scale: 0.9 }}
                      whileInView={{ opacity: 1, y: 0, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.6, delay: index * 0.1 }}
                      whileHover={{ 
                        y: -15,
                        transition: { duration: 0.3 }
                      }}
                      style={{ height: '100%' }}
                    >
                      <Card
                        sx={{
                          height: '100%',
                          p: { xs: 3, md: 4 },
                          borderRadius: 4,
                          backgroundColor: alpha('#fff', 0.95),
                          backdropFilter: 'blur(20px)',
                          border: `2px solid ${alpha(step.color, 0.2)}`,
                          boxShadow: `
                            0 10px 40px ${alpha(step.color, 0.1)},
                            inset 0 1px 0 ${alpha('#fff', 0.2)}
                          `,
                          position: 'relative',
                          overflow: 'visible',
                          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: alpha('#fff', 1),
                            borderColor: step.color,
                            boxShadow: `
                              0 25px 60px ${alpha(step.color, 0.25)},
                              inset 0 1px 0 ${alpha('#fff', 0.3)}
                            `,
                            transform: 'translateY(-8px)',
                          },
                        }}
                        onClick={() => setActiveStep(index)}
                      >
                        {/* Step Number Badge */}
                        <motion.div
                          animate={{ 
                            backgroundColor: activeStep === index ? step.color : alpha(step.color, 0.1),
                            color: activeStep === index ? 'white' : step.color,
                          }}
                          transition={{ duration: 0.3 }}
                          style={{
                            position: 'absolute',
                            top: -20,
                            left: 20,
                            width: 50,
                            height: 50,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 'bold',
                            fontSize: '1.2rem',
                            boxShadow: '0 8px 25px rgba(0,0,0,0.2)',
                            zIndex: 2,
                          }}
                        >
                          {step.number}
                        </motion.div>
                        
                        {/* Icon Container with Glow Effect */}
                        <motion.div
                          animate={{ scale: activeStep === index ? 1.1 : 1 }}
                          transition={{ duration: 0.3 }}
                        >
                          <Box
                            sx={{
                              width: 80,
                              height: 80,
                              borderRadius: '50%',
                              background: `linear-gradient(135deg, ${step.color} 0%, ${alpha(step.color, 0.7)} 100%)`,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              margin: '0 auto 24px',
                              color: 'white',
                              fontSize: 40,
                              boxShadow: `0 15px 35px ${alpha(step.color, 0.4)}`,
                              position: 'relative',
                              '&::after': {
                                content: '""',
                                position: 'absolute',
                                top: -5,
                                left: -5,
                                right: -5,
                                bottom: -5,
                                borderRadius: '50%',
                                background: `radial-gradient(circle, ${alpha(step.color, 0.3)} 0%, transparent 70%)`,
                                zIndex: -1,
                              }
                            }}
                          >
                            {step.icon}
                          </Box>
                        </motion.div>
                        
                        {/* Content */}
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography 
                            variant="h5" 
                            fontWeight="bold" 
                            sx={{ 
                              mb: 2,
                              color: 'text.primary',
                              fontSize: { xs: '1.25rem', md: '1.5rem' },
                            }}
                          >
                            {step.title}
                          </Typography>
                          
                          <Typography 
                            variant="body1" 
                            sx={{ 
                              mb: 3, 
                              color: 'text.secondary',
                              fontSize: { xs: '0.95rem', md: '1rem' },
                              lineHeight: 1.6,
                            }}
                          >
                            {step.description}
                          </Typography>
                          
                          {/* Duration Badge */}
                          <Box
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 1,
                              px: 2,
                              py: 1,
                              borderRadius: 20,
                              backgroundColor: alpha(step.color, 0.1),
                              border: `1px solid ${alpha(step.color, 0.2)}`,
                            }}
                          >
                            <AccessTime sx={{ fontSize: 18, color: step.color }} />
                            <Typography 
                              variant="caption" 
                              fontWeight="bold"
                              sx={{ color: step.color }}
                            >
                              {step.duration}
                            </Typography>
                          </Box>
                          
                          {/* Details List - Shows on hover */}
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ 
                              opacity: activeStep === index ? 1 : 0,
                              height: activeStep === index ? 'auto' : 0,
                            }}
                            transition={{ duration: 0.3 }}
                            style={{ overflow: 'hidden' }}
                          >
                            <Box sx={{ mt: 3, pt: 3, borderTop: `1px dashed ${alpha(step.color, 0.2)}` }}>
                              <Typography variant="body2" fontWeight="bold" sx={{ mb: 1.5, color: step.color }}>
                                Includes:
                              </Typography>
                              <Stack spacing={1}>
                                {step.details.map((detail, idx) => (
                                  <Box 
                                    key={idx} 
                                    sx={{ 
                                      display: 'flex', 
                                      alignItems: 'center', 
                                      gap: 1,
                                    }}
                                  >
                                    <CheckCircle sx={{ fontSize: 16, color: step.color }} />
                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                      {detail}
                                    </Typography>
                                  </Box>
                                ))}
                              </Stack>
                            </Box>
                          </motion.div>
                        </Box>
                      </Card>
                    </motion.div>
                  </Grid>
                ))}
              </Grid>
              
              {/* Active Step Indicator */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
                style={{
                  position: 'absolute',
                  bottom: -40,
                  left: '50%',
                  transform: 'translateX(-50%)',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  {tradingSteps.map((_, index) => (
                    <Box
                      key={index}
                      onClick={() => setActiveStep(index)}
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        backgroundColor: activeStep === index ? '#ffeb3b' : alpha('#fff', 0.3),
                        cursor: 'pointer',
                        transition: 'all 0.3s',
                        '&:hover': {
                          backgroundColor: activeStep === index ? '#ffeb3b' : alpha('#fff', 0.5),
                          transform: 'scale(1.2)',
                        },
                      }}
                    />
                  ))}
                </Box>
              </motion.div>
            </Box>
          )}
          
          {/* Mobile Layout - Modern Cards */}
          {isMobile && (
            <Box>
              <Stack spacing={3}>
                {tradingSteps.map((step, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >
                    <Card
                      sx={{
                        p: 3,
                        borderRadius: 3,
                        backgroundColor: alpha('#fff', 0.95),
                        backdropFilter: 'blur(10px)',
                        border: `2px solid ${alpha(step.color, 0.2)}`,
                        boxShadow: `0 10px 30px ${alpha(step.color, 0.1)}`,
                      }}
                      onClick={() => setActiveStep(index)}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                        {/* Step Number */}
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: '50%',
                            backgroundColor: activeStep === index ? step.color : alpha(step.color, 0.1),
                            color: activeStep === index ? 'white' : step.color,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 'bold',
                            fontSize: '1rem',
                            flexShrink: 0,
                          }}
                        >
                          {step.number}
                        </Box>
                        
                        {/* Content */}
                        <Box sx={{ flex: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                            <Box
                              sx={{
                                width: 40,
                                height: 40,
                                borderRadius: '50%',
                                background: `linear-gradient(135deg, ${step.color} 0%, ${alpha(step.color, 0.7)} 100%)`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                fontSize: 20,
                              }}
                            >
                              {step.icon}
                            </Box>
                            <Typography variant="h6" fontWeight="bold">
                              {step.title}
                            </Typography>
                          </Box>
                          
                          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                            {step.description}
                          </Typography>
                          
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <AccessTime sx={{ fontSize: 16, color: step.color }} />
                              <Typography variant="caption" sx={{ color: step.color }}>
                                {step.duration}
                              </Typography>
                            </Box>
                            
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              {step.details.slice(0, 2).map((_, idx) => (
                                <Box
                                  key={idx}
                                  sx={{
                                    width: 6,
                                    height: 6,
                                    borderRadius: '50%',
                                    backgroundColor: alpha(step.color, 0.3),
                                  }}
                                />
                              ))}
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    </Card>
                  </motion.div>
                ))}
              </Stack>
            </Box>
          )}
          
          {/* CTA Button - Modern Design */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            <Box sx={{ textAlign: 'center', mt: { xs: 6, md: 10 } }}>
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Button
                  component={Link}
                  to="/register"
                  variant="contained"
                  size="large"
                  sx={{
                    background: 'linear-gradient(90deg, #ffeb3b 0%, #ff9800 100%)',
                    color: '#1b5e20',
                    fontWeight: 800,
                    borderRadius: 3,
                    px: { xs: 4, md: 8 },
                    py: { xs: 1.5, md: 2 },
                    fontSize: { xs: '1rem', md: '1.25rem' },
                    boxShadow: '0 20px 50px rgba(255, 235, 59, 0.4)',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: '-100%',
                      width: '100%',
                      height: '100%',
                      background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
                      transition: '0.5s',
                    },
                    '&:hover': {
                      background: 'linear-gradient(90deg, #ff9800 0%, #ffeb3b 100%)',
                      boxShadow: '0 25px 60px rgba(255, 235, 59, 0.6)',
                      '&::before': {
                        left: '100%',
                      },
                    },
                  }}
                  startIcon={<RocketLaunch sx={{ fontSize: { xs: 24, md: 28 } }} />}
                >
                  Start Your Trading Journey
                </Button>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.7 }}
              >
                <Typography 
                  variant="body2" 
                  sx={{ 
                    mt: 3, 
                    color: alpha('#fff', 0.7),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                  }}
                >
                  <CheckCircle sx={{ fontSize: 16, color: '#ffeb3b' }} />
                  Average setup time: 10 minutes • No credit card required
                </Typography>
              </motion.div>
            </Box>
          </motion.div>
        </Container>
      </Box>

      {/* Testimonials */}
      <Container maxWidth="xl" sx={{ py: { xs: 6, md: 10 }, px: { xs: 2, sm: 3, md: 4 } }}>
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 8 } }}>
            <Chip 
              label="TESTIMONIALS" 
              sx={{ 
                backgroundColor: alpha('#ff9800', 0.1),
                color: '#ff9800',
                fontWeight: 600,
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                mb: 2,
                py: 0.8,
              }}
            />
            <Typography 
              variant={isMobile ? "h4" : "h2"} 
              gutterBottom 
              sx={{ 
                fontWeight: 'bold',
                fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' }
              }}
            >
              Trusted by{' '}
              <Box component="span" sx={{ color: '#ff9800' }}>
                Global Traders
              </Box>
            </Typography>
            <Typography 
              variant={isMobile ? "body1" : "h6"} 
              color="text.secondary"
              sx={{ maxWidth: 700, mx: 'auto', fontSize: { xs: '0.875rem', sm: '1rem', md: '1.25rem' } }}
            >
              See what our community says about their trading experience
            </Typography>
          </Box>
        </motion.div>
        
        <Grid container spacing={3}>
          {testimonials.map((testimonial, index) => (
            <Grid item xs={12} md={4} key={index}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
              >
                <Card
                  sx={{
                    p: 3,
                    borderRadius: 4,
                    height: '100%',
                    border: '1px solid',
                    borderColor: 'grey.200',
                    transition: 'all 0.3s',
                    '&:hover': {
                      borderColor: '#ff9800',
                      boxShadow: '0 15px 30px rgba(255, 152, 0, 0.1)',
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{ bgcolor: '#ff9800', width: 40, height: 40, fontSize: '0.875rem' }}>
                        {testimonial.avatar}
                      </Avatar>
                      <Box>
                        <Typography variant="h6" fontWeight="bold" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                          {testimonial.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                          {testimonial.role}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ ml: 'auto', display: 'flex', gap: 0.5 }}>
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star key={i} sx={{ color: '#ff9800', fontSize: 18 }} />
                      ))}
                    </Box>
                  </Box>
                  
                  <Typography variant="body1" color="text.primary" sx={{ mb: 2, fontStyle: 'italic', fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                    "{testimonial.content}"
                  </Typography>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* CTA Section */}
      <Container maxWidth="xl" sx={{ mb: { xs: 6, md: 10 }, px: { xs: 2, sm: 3, md: 4 } }}>
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <Paper
            sx={{
              background: 'linear-gradient(135deg, #1b5e20 0%, #2e7d32 30%, #4caf50 100%)',
              color: 'white',
              p: { xs: 3, md: 6 },
              borderRadius: 4,
              textAlign: 'center',
              position: 'relative',
              overflow: 'hidden',
              boxShadow: '0 30px 60px rgba(76, 175, 80, 0.3)',
            }}
          >
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <Typography 
                variant={isMobile ? "h4" : "h2"} 
                gutterBottom 
                fontWeight="bold"
                sx={{ 
                  mb: 2,
                  fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' },
                  lineHeight: 1.2,
                }}
              >
                Ready to Transform Your{' '}
                <Box component="span" sx={{ color: '#ffeb3b' }}>
                  Fruit Business?
                </Box>
              </Typography>
              
              <Typography 
                variant={isMobile ? "body1" : "h6"} 
                paragraph 
                sx={{ 
                  mb: 4, 
                  opacity: 0.9,
                  maxWidth: 800,
                  mx: 'auto',
                  fontSize: { xs: '0.875rem', sm: '1rem', md: '1.25rem' }
                }}
              >
                Join thousands of successful traders who trust our platform for secure, 
                efficient, and profitable fruit trading.
              </Typography>
              
              <Stack 
                direction={{ xs: 'column', sm: 'row' }} 
                spacing={2} 
                justifyContent="center"
                alignItems="center"
              >
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Button
                    component={Link}
                    to="/register"
                    variant="contained"
                    size={isMobile ? "medium" : "large"}
                    sx={{
                      backgroundColor: '#ffeb3b',
                      color: '#1b5e20',
                      fontWeight: 'bold',
                      borderRadius: 3,
                      px: { xs: 3, sm: 5 },
                      py: 1.2,
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      boxShadow: '0 10px 30px rgba(255, 235, 59, 0.3)',
                      '&:hover': {
                        backgroundColor: '#ffd600',
                      },
                    }}
                    startIcon={<RocketLaunch />}
                  >
                    Start Free Trial
                  </Button>
                </motion.div>
                
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Button
                    component={Link}
                    to="/demo"
                    variant="outlined"
                    size={isMobile ? "medium" : "large"}
                    sx={{
                      borderColor: alpha('#fff', 0.3),
                      borderWidth: 2,
                      color: 'white',
                      borderRadius: 3,
                      px: { xs: 3, sm: 5 },
                      py: 1.2,
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      backdropFilter: 'blur(10px)',
                      '&:hover': {
                        borderColor: alpha('#fff', 0.6),
                        backgroundColor: alpha('#fff', 0.1),
                      },
                    }}
                    startIcon={<PlayArrow />}
                  >
                    Watch Demo
                  </Button>
                </motion.div>
              </Stack>
            </Box>
          </Paper>
        </motion.div>
      </Container>
    </Box>
  );
};

export default Home;